// Shared cycle rules for the performance contract flow.
//
// The cohort question — "which roles actually belong to the cycle currently at
// globalMaxPid?" — is asked in two places: getLoadNewList (MyTeamPerformance)
// decides whether to offer a stub or show the blocked popup, and
// getNextPerformanceId (CreateEditPerformance) decides which cycle a saved
// contract lands in. There is no DB constraint on
// (apraiseemailid, roletype, performanceid), so if the two sides disagree a
// duplicate contract is written and nothing rejects it. Keep them on this
// shared logic.

// Change strings written to insyts_appraisalinformation_history by the
// appraisal-information editor.
const CHANGE_ROW_ADDED = "Row added";
const CHANGE_ROW_DELETED = "Row deleted";
const CHANGE_APPRAISAL_ENABLED = "Appraisal: From No to Yes";

// When was this role last brought BACK after having been removed?
//
// A role's first-ever "Row added" is a genuine new addition and must NOT count
// as a re-enable — that role should catch up into the current cycle normally.
// Only an add that follows a previous removal means "this role sat out and has
// now returned". Gating on a prior *deletion* (not a prior add) is what makes
// the textbook [Row deleted, Row added] shape register.
export const getLatestReEnabledAt = (historyList, roletype) => {
  const roleHistory = (historyList || [])
    .filter((h) => h?.roletype === roletype)
    .sort((a, b) => new Date(a.modifiedat) - new Date(b.modifiedat));

  let sawRowDeleted = false;
  let latestReEnabledAt = 0;

  for (const h of roleHistory) {
    const t = h?.modifiedat ? new Date(h.modifiedat).getTime() : 0;
    if (h?.change === CHANGE_ROW_DELETED) {
      sawRowDeleted = true;
    } else if (h?.change === CHANGE_ROW_ADDED) {
      if (sawRowDeleted) latestReEnabledAt = Math.max(latestReEnabledAt, t);
    } else if (h?.change === CHANGE_APPRAISAL_ENABLED) {
      latestReEnabledAt = Math.max(latestReEnabledAt, t);
    }
  }

  return latestReEnabledAt;
};

// Latest reviewedon across the given contracts (0 when none are reviewed).
export const getLatestReviewedAt = (contracts) =>
  (contracts || []).reduce((latest, c) => {
    const t = c?.reviewedon ? new Date(c.reviewedon).getTime() : 0;
    return Math.max(latest, t);
  }, 0);

// A role re-added AFTER the cycle at globalMaxPid already closed is a fresh
// joiner for the NEXT cycle, not a lagging member of the closed one — it must
// not hold that cycle open. This matters because the roster snapshot on a
// reviewed contract is re-fetched live at review time, so a role deleted
// moments after the last review stays frozen in the snapshot and would
// otherwise look like a permanently-pending cohort member.
export const isReEnabledAfterCycleClosed = (
  historyList,
  roletype,
  latestSiblingReviewedAt
) => {
  if (!latestSiblingReviewedAt) return false;
  return getLatestReEnabledAt(historyList, roletype) > latestSiblingReviewedAt;
};
